import React, { useEffect, useId, useRef, useState } from 'react';
import './ResponsiveNavigation.css';

type Props = {
  children: React.ReactNode;
  sidebarClassName: string;
};

export const ResponsiveNavigation: React.FC<Props> = ({ children, sidebarClassName }) => {
  const [narrow, setNarrow] = useState(() => window.innerWidth <= 980);
  const [open, setOpen] = useState(false);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dialogId = useId();

  useEffect(() => {
    const update = () => setNarrow(window.innerWidth <= 980);
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (narrow && open && dialog && !dialog.open) dialog.showModal();
    if (!open && dialog?.open) dialog.close();
  }, [narrow, open]);

  useEffect(() => { if (!narrow) setOpen(false); }, [narrow]);

  const close = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };
  const content = (
    <aside className={sidebarClassName} aria-label="Portal navigation"
      onClickCapture={event => {
        if (narrow && (event.target as HTMLElement).closest('.nav-item, .sa-nav-item')) close();
      }}>
      {children}
    </aside>
  );

  if (!narrow) return content;

  return (
    <>
      <button ref={triggerRef} type="button" className="responsive-navigation-trigger"
        aria-label="Open navigation menu" aria-haspopup="dialog" aria-expanded={open}
        aria-controls={dialogId} onClick={() => setOpen(true)}>
        <span aria-hidden="true">&#9776;</span> Menu
      </button>
      <dialog ref={dialogRef} id={dialogId} className="responsive-navigation-dialog"
        aria-label="Navigation menu" onCancel={close} onClose={close}
        onKeyDown={event => {
          if (event.key === 'Escape') { event.preventDefault(); close(); }
        }}
        onClick={event => {
          if (event.target !== event.currentTarget) return;
          const bounds = event.currentTarget.getBoundingClientRect();
          if (event.clientX < bounds.left || event.clientX > bounds.right ||
              event.clientY < bounds.top || event.clientY > bounds.bottom) close();
        }}>
        <div className="responsive-navigation-heading">
          <span>Navigation</span>
          <button type="button" aria-label="Close navigation menu" onClick={close}>Close <span aria-hidden="true">&times;</span></button>
        </div>
        {content}
      </dialog>
    </>
  );
};
